import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAdminReports, useUpdateReport } from '@/hooks/useReports';
import { useModeratePost } from '@/hooks/useAdmin';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Flag, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  reviewed: { label: 'Reviewed', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  dismissed: { label: 'Dismissed', className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' },
};

export default function AdminReports() {
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const { data: reports, isLoading } = useAdminReports();
  const updateReport = useUpdateReport();
  const moderatePost = useModeratePost();
  const { toast } = useToast();

  const filteredReports = reports?.filter(
    (r) => statusFilter === 'all' || r.status === statusFilter
  );

  const handleDismiss = async (reportId: string) => {
    try {
      await updateReport.mutateAsync({ reportId, status: 'dismissed' });
      toast({ title: 'Report dismissed' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleReview = async (reportId: string) => {
    try {
      await updateReport.mutateAsync({ reportId, status: 'reviewed' });
      toast({ title: 'Report marked as reviewed' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleDeletePost = async (reportId: string, postId: string) => {
    try {
      await moderatePost.mutateAsync({ postId, action: 'delete' });
      await updateReport.mutateAsync({ reportId, status: 'reviewed' });
      toast({ title: 'Post deleted and report resolved' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const pendingCount = reports?.filter((r) => r.status === 'pending').length || 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Reports</h1>
        <p className="text-muted-foreground text-sm">
          Review and action member reports
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Flag className="h-5 w-5" />
                Reports
                {pendingCount > 0 && (
                  <Badge variant="destructive">{pendingCount} pending</Badge>
                )}
              </CardTitle>
              <CardDescription>
                {reports?.length || 0} total reports
              </CardDescription>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredReports && filteredReports.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reporter</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReports.map((report) => {
                    const statusConfig = STATUS_CONFIG[report.status] || STATUS_CONFIG.pending;

                    return (
                      <TableRow key={report.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <SignedAvatarImage src={report.reporter_profile?.avatar_url || undefined} />
                              <AvatarFallback className="text-xs">
                                {report.reporter_profile?.full_name?.charAt(0) || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm">{report.reporter_profile?.full_name || 'Unknown'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {report.reported_post_id ? (
                            <Badge variant="outline">Post</Badge>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">User</Badge>
                              <span className="text-sm text-muted-foreground">
                                {report.reported_user_profile?.full_name || ''}
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium">{report.reason}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground max-w-[200px] truncate block">
                            {report.details || report.reported_post?.content?.substring(0, 80) || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.className}>
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}
                        </TableCell>
                        <TableCell>
                          {report.status === 'pending' && (
                            <div className="flex items-center gap-1">
                              {report.reported_post_id && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeletePost(report.id, report.reported_post_id!)}
                                  disabled={updateReport.isPending || moderatePost.isPending}
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Delete Post
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleReview(report.id)}
                                disabled={updateReport.isPending}
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Reviewed
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDismiss(report.id)}
                                disabled={updateReport.isPending}
                              >
                                <XCircle className="h-3 w-3 mr-1" />
                                Dismiss
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Flag className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">
                {statusFilter === 'pending' ? 'No pending reports.' : 'No reports found.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
